/* eslint-disable prefer-destructuring */
import axios from 'axios';
import * as HttpStatus from 'http-status-codes';
import { z } from 'zod';
// eslint-disable-next-line import/no-cycle
import { logout } from '../Login/LoginAction';
import { fetchOrganisations } from '../../scenes/Organisation/OrganisationAction';
import { fetchPlans } from '../../scenes/Dashboard/scenes/Plan/PlanAction';
import { fetchUser } from '../../scenes/Admin/scenes/User/UserActions';
import { orgIdSelector } from '../../scenes/Organisation/OrganisationSelectors';
import { secureStorage } from '../../utils/secureStorage';
import { validateUrlParams, urlParamSchemas } from '../../utils/urlValidation';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_AUTH_TOKEN = 'auth/token';
const URL_TOKEN_EXCLUDED_PATHS = ['/confirmation/email', '/reset-password'];

export const types = {
  APP_INITIALIZING: 'app/APP_INITIALIZING',
  APP_INITIALIZED: 'app/APP_INITIALIZED'
};

const isBase64 = str => {
  if (!str || !str.trim()) {
    return false;
  }
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
};

/**
 * Different types of URLs with token:
 *
 * 1. `token`: JWT token. If API key is not presented, use the default one.
 * 2. `token`: Base64 encoded LinkGen parameters acquired from LMI authentication service. API key MUST be presented in query string.
 * 3. `key` and `EncryptedData`: Raw LinkGen parameters. Usually sent from LMI services. If API key is not presented, use the default one.
 *
 */
class AccessRequestUrl {
  constructor() {
    this.url = new URL(window.location);
  }

  get linkgenToken() {
    console.log('--- Getting LinkGen parameters');

    // Handle raw LinkGen parameters
    const key = this.url.searchParams.get('key');
    const encryptedData = this.url.searchParams.get('EncryptedData');
    if (key && encryptedData) {
      const validation = validateUrlParams(
        { key, EncryptedData: encryptedData },
        z.object({
          key: urlParamSchemas.key,
          EncryptedData: urlParamSchemas.EncryptedData
        })
      );
      
      if (!validation) {
        console.warn('Invalid LinkGen parameters detected');
        return undefined;
      }
      
      return btoa(`key=${key}&EncryptedData=${encodeURIComponent(encryptedData)}`);
    }

    console.log('--- Getting base64 LinkGen parameters');
    // Handle base64 encoded LinkGen parameters
    const token = this.url.searchParams.get('linkgen');
    if (token) {
      const validation = validateUrlParams(
        { linkgen: token },
        z.object({ linkgen: urlParamSchemas.linkgen })
      );
      
      if (!validation || !isBase64(token)) {
        console.warn('Invalid linkgen token detected');
        return undefined;
      }
      
      return token;
    }

    return undefined;
  }

  get token() {
    if (URL_TOKEN_EXCLUDED_PATHS.includes(this.url.pathname)) {
      return secureStorage.getItem('token');
    }
    
    const urlToken = this.url.searchParams.get('token');
    if (urlToken) {
      const validation = validateUrlParams(
        { token: urlToken },
        z.object({ token: urlParamSchemas.token })
      );
      
      if (!validation) {
        console.warn('Invalid token parameter detected');
        return secureStorage.getItem('token');
      }
      
      return urlToken;
    }
    
    return secureStorage.getItem('token');
  }
}

export const initialize = () => async (dispatch, getState) => {
  dispatch({ type: types.APP_INITIALIZING });

  axios.interceptors.response.use(
    res => res,
    error => {
      // Axios v1.x: error.response contains the response object
      const response = error?.response;
      const status = response?.status;
      switch (status) {
        case HttpStatus.UNAUTHORIZED:
          dispatch(logout());
          return Promise.reject(response || error);
        case HttpStatus.PAYMENT_REQUIRED:
          return Promise.reject(response || error);
        case HttpStatus.EXPECTATION_FAILED:
          return Promise.reject(error);
        default:
          return Promise.reject(response || error);
      }
    }
  );

  const url = new AccessRequestUrl();

  // first check if the current token is expired
  const expiry = secureStorage.getItem('expiry');
  if (expiry && expiry < Date.now()) {
    dispatch(logout());
  } else {
    // Check for LinkGen access
    let token = url.linkgenToken;
    if (token) {
      console.log('--- Found LinkGen token in URL');
      // axios.defaults.headers.common.Authorization = `LinkGen ${token}`;
      try {
        console.log('--- Exchange for JWT token');
        const res = await axios.get(`${API_BASE}${API_AUTH_TOKEN}`, {
          headers: {
            Authorization: `LinkGen ${token}`
          }
        });
        token = res.data.access_token;
        secureStorage.setItem('token', token);
        secureStorage.setItem('expiry', res.data.expiry);
      } catch (err) {
        console.error('--- Failed to exchange for JWT token', err);
        return;
      }
    } else {
      console.log('--- Getting JWT token from query string or local storage');
      token = url.token;
      // No need to get JWT token again
    }

    // After dealing with all different types of authentication methods, the token, if applicable, is finally settled at this stage.
    // So persist to local storage and bind to axios's Authorization header
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;

    if (token) {
      try {
        await Promise.all([dispatch(fetchOrganisations()), dispatch(fetchUser())]);
        const orgId = orgIdSelector(getState());
        if (orgId) await dispatch(fetchPlans(orgId));

        // Login from the public landing page should enter the app. A refresh on
        // /dashboard/plans (or any other in-app route) must stay put.
        const path = window.location.pathname;
        if (path === '/' || path === '') {
          window.history.replaceState(null, '', '/dashboard');
        }
      } catch (err) {
        console.log(err);
      }
    }
  }
  dispatch({ type: types.APP_INITIALIZED });
};

const htmlTemplate = htmlBody =>
  htmlBody
    ? `
    <html>
      <head>
        <meta charset="utf-8">
        ${
          process.env.NODE_ENV === 'development'
            ? [...document.head.querySelectorAll('style')].map(item => item.outerHTML).join('')
            : [...document.head.querySelectorAll('link[rel="stylesheet"]')]
                .map(item => `<link href="${item.href}" rel="stylesheet" />`)
                .join('')
        }
        <title></title>
      </head>
      <body>
        ${htmlBody}
      </body>
    </html>`
    : null;

export const download = (content, cover, params) => () => {
  const url = process.env.REACT_APP_API_EXPORT_PDF;
  const headers = {};
  if (process.env.REACT_APP_APIM_KEY) {
    headers['Ocp-Apim-Subscription-Key'] = process.env.REACT_APP_APIM_KEY;
  }
  return axios.post(
    url,
    { content: htmlTemplate(content), frontMatter: htmlTemplate(cover), format: 'A4', ...params },
    { responseType: 'arraybuffer', headers }
  );
};
